/**
 * CR173: Unified Preview Configuration Store
 * 
 * Single source of truth for all preview-related configuration across:
 * - Theme Labs (global tokens)
 * - Layout Studio (page layout + deal card UX)
 * - Deal Cards (per-type templates and registry)
 * 
 * Uses a simple pub/sub pattern for React integration without external deps.
 */

import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import type { LayoutJson } from "@/lib/layout-studio/types";
import type { DealCardUXConfig } from "@/lib/layout-studio/dealCardUXTypes";
// CardTemplateJson type not used directly but referenced in comments for context

// ================================================================
// TYPES
// ================================================================

export type PreviewUpdateSource = 
  | "theme_labs" 
  | "layout_studio" 
  | "deal_cards" 
  | "preview_controls"
  | "initialization";

export interface DealCardOverrides {
  /** Per-type registry overrides (ribbon, enabled, etc.) */
  registry?: {
    ribbon_text?: string;
    ribbon_theme?: string;
    is_enabled?: boolean;
  };
  /** Per-type template overrides - flexible structure for admin panel compatibility */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  template?: any;
}

export interface PreviewConfig {
  /** Theme Labs: Global design tokens */
  globalTheme: GlobalThemeTokens | null;
  
  /** Layout Studio: Full layout config */
  layout: LayoutJson | null;
  
  /** Layout Studio: Extracted deal card UX config */
  dealCardUX: DealCardUXConfig | null;
  
  /** Deal Cards: Per-type overrides keyed by deal type */
  dealCardOverrides: Record<string, DealCardOverrides>;
  
  /** Selected deal type for Deal Cards tab preview */
  activeDealType: string | null;
  
  /** Preview mode: draft vs published */
  previewSource: "draft" | "published_staging" | "published_live";
  
  /** Device viewport */
  viewport: "desktop" | "mobile";
  
  /** Debug: Last update timestamp */
  lastUpdatedAt: number;
  
  /** Debug: Source of last update */
  lastUpdatedBy: PreviewUpdateSource;
  
  /** Debug: Version counter for tracking updates */
  version: number;
}

// ================================================================
// DEFAULT CONFIG
// ================================================================

const DEFAULT_PREVIEW_CONFIG: PreviewConfig = {
  globalTheme: null,
  layout: null,
  dealCardUX: null,
  dealCardOverrides: {},
  activeDealType: null,
  previewSource: "draft",
  viewport: "desktop",
  lastUpdatedAt: Date.now(),
  lastUpdatedBy: "initialization",
  version: 0,
};

// ================================================================
// STORE IMPLEMENTATION
// ================================================================

type Listener = (config: PreviewConfig, patch: Partial<PreviewConfig>) => void;

class PreviewConfigStore {
  private config: PreviewConfig = { ...DEFAULT_PREVIEW_CONFIG };
  private listeners: Set<Listener> = new Set();
  
  /**
   * Get current preview config snapshot
   */
  getConfig(): PreviewConfig {
    return this.config;
  }
  
  /**
   * Update preview config with a partial patch
   * @param patch - Partial config to merge
   * @param source - Source of the update for debugging
   */
  updateConfig(patch: Partial<PreviewConfig>, source: PreviewUpdateSource): void {
    const now = Date.now();
    
    // Create new config object (immutable update)
    this.config = {
      ...this.config,
      ...patch,
      lastUpdatedAt: now,
      lastUpdatedBy: source,
      version: this.config.version + 1,
    };
    
    console.log(`[CR173-Store] Config updated by ${source}`, {
      version: this.config.version,
      patchKeys: Object.keys(patch),
      timestamp: new Date(now).toISOString(),
    });
    
    // Notify all listeners
    this.notifyListeners(patch);
  }
  
  /**
   * Update global theme tokens (from Theme Labs)
   */
  setGlobalTheme(tokens: GlobalThemeTokens | null): void {
    this.updateConfig({ globalTheme: tokens }, "theme_labs");
  }
  
  /**
   * Update layout config (from Layout Studio)
   */
  setLayout(layout: LayoutJson | null): void {
    this.updateConfig({ 
      layout, 
      dealCardUX: layout?.dealCardUX ?? null,
    }, "layout_studio");
  }
  
  /**
   * Update deal card UX config only (from Layout Studio Deal Cards tab)
   */
  setDealCardUX(ux: DealCardUXConfig | null): void {
    this.updateConfig({ dealCardUX: ux }, "layout_studio");
  }
  
  /**
   * Update per-type deal card overrides (from Deal Cards tab)
   */
  setDealCardOverrides(dealType: string, overrides: DealCardOverrides): void {
    this.updateConfig({
      dealCardOverrides: {
        ...this.config.dealCardOverrides,
        [dealType]: overrides,
      },
      activeDealType: dealType,
    }, "deal_cards");
  }
  
  /**
   * Update preview controls (viewport, source)
   */
  setPreviewControls(controls: { viewport?: "desktop" | "mobile"; previewSource?: PreviewConfig["previewSource"] }): void {
    this.updateConfig(controls, "preview_controls");
  }
  
  /**
   * Subscribe to config changes
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  
  /**
   * Notify all listeners of update
   */
  private notifyListeners(patch: Partial<PreviewConfig>): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.config, patch);
      } catch (err) {
        console.error("[CR173-Store] Listener error:", err);
      }
    });
  }
  
  /**
   * Reset to defaults (for testing)
   */
  reset(): void {
    this.config = { ...DEFAULT_PREVIEW_CONFIG };
    this.notifyListeners(this.config);
  }
  
  /**
   * Get debug snapshot for preview debug panel
   */
  getDebugSnapshot(): {
    version: number;
    lastUpdatedAt: string;
    lastUpdatedBy: PreviewUpdateSource;
    hasGlobalTheme: boolean;
    hasLayout: boolean;
    hasDealCardUX: boolean;
    dealTypeOverrideCount: number;
    activeDealType: string | null;
    previewSource: string;
    viewport: string;
  } {
    return {
      version: this.config.version,
      lastUpdatedAt: new Date(this.config.lastUpdatedAt).toISOString(),
      lastUpdatedBy: this.config.lastUpdatedBy,
      hasGlobalTheme: this.config.globalTheme !== null,
      hasLayout: this.config.layout !== null,
      hasDealCardUX: this.config.dealCardUX !== null,
      dealTypeOverrideCount: Object.keys(this.config.dealCardOverrides).length,
      activeDealType: this.config.activeDealType,
      previewSource: this.config.previewSource,
      viewport: this.config.viewport,
    };
  }
}

// ================================================================
// SINGLETON EXPORT
// ================================================================

export const previewConfigStore = new PreviewConfigStore();

// ================================================================
// COMPUTE EFFECTIVE CONFIG (Merge Strategy)
// ================================================================

/**
 * CR173: Deterministic merge strategy for preview rendering
 * 
 * Precedence (highest to lowest):
 * 1. Deal Card type-specific overrides (template)
 * 2. Layout Studio overrides (dealCardUX)
 * 3. Theme Labs global tokens
 * 4. Hard defaults
 */
export function computeEffectivePreviewConfig(config: PreviewConfig): {
  theme: GlobalThemeTokens | null;
  layout: LayoutJson | null;
  dealCardUX: DealCardUXConfig | null;
  activeTypeOverrides: DealCardOverrides | null;
} {
  const activeTypeOverrides = config.activeDealType 
    ? config.dealCardOverrides[config.activeDealType] ?? null
    : null;
  
  return {
    theme: config.globalTheme,
    layout: config.layout,
    dealCardUX: config.dealCardUX,
    activeTypeOverrides,
  };
}
